import { useState, useRef, useEffect, useCallback } from "react";

// ── Constants ──────────────────────────────────────────────────────────────

const INSTRUMENT_CONFIGS = {
  guitar: {
    label: "Guitar",
    strings: [
      { label: "E", octave: "2", freq: 82.41 },
      { label: "A", octave: "2", freq: 110.00 },
      { label: "D", octave: "3", freq: 146.83 },
      { label: "G", octave: "3", freq: 196.00 },
      { label: "B", octave: "3", freq: 246.94 },
      { label: "E", octave: "4", freq: 329.63 },
    ],
  },
  "bass-4": {
    label: "Bass 4",
    strings: [
      { label: "E", octave: "1", freq: 41.20 },
      { label: "A", octave: "1", freq: 55.00 },
      { label: "D", octave: "2", freq: 73.42 },
      { label: "G", octave: "2", freq: 98.00 },
    ],
  },
  "bass-5": {
    label: "Bass 5",
    strings: [
      { label: "B", octave: "0", freq: 30.87 },
      { label: "E", octave: "1", freq: 41.20 },
      { label: "A", octave: "1", freq: 55.00 },
      { label: "D", octave: "2", freq: 73.42 },
      { label: "G", octave: "2", freq: 98.00 },
    ],
  },
};

const NOTE_NAMES = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];
const STORAGE_KEY = "strumiq_last_instrument";

// ── Helpers ────────────────────────────────────────────────────────────────

function freqToNote(freq) {
  const midi = 69 + 12 * Math.log2(freq / 440);
  const nearestMidi = Math.round(midi);
  const cents = (midi - nearestMidi) * 100;
  const name = NOTE_NAMES[((nearestMidi % 12) + 12) % 12];
  const octave = Math.floor(nearestMidi / 12) - 1;
  return { name, octave, cents, midi: nearestMidi };
}

function freqToCentsFromTarget(freq, targetFreq) {
  return 1200 * Math.log2(freq / targetFreq);
}

function centsToColor(cents) {
  const abs = Math.abs(cents);
  if (abs <= 5) return "#22c55e";
  if (abs <= 10) return "#f59e0b";
  return "#ef4444";
}

// ── YIN pitch detection ────────────────────────────────────────────────────

function yinDetect(buffer, sampleRate) {
  const bufSize = buffer.length;
  const halfSize = Math.floor(bufSize / 2);

  // Noise gate — very low to catch unplugged electric bass (quiet acoustic signal)
  let rms = 0;
  for (let i = 0; i < bufSize; i++) rms += buffer[i] * buffer[i];
  rms = Math.sqrt(rms / bufSize);
  if (rms < 0.002) return null;

  // YIN difference function
  const yin = new Float32Array(halfSize);
  yin[0] = 1;
  let runningSum = 0;

  for (let tau = 1; tau < halfSize; tau++) {
    let diff = 0;
    for (let j = 0; j < halfSize; j++) {
      const delta = buffer[j] - buffer[j + tau];
      diff += delta * delta;
    }
    yin[tau] = diff;
    runningSum += diff;
    yin[tau] = runningSum > 0 ? (yin[tau] * tau) / runningSum : 1;
  }

  // Find first tau below absolute threshold 0.125
  let tau = -1;
  for (let t = 2; t < halfSize - 1; t++) {
    if (yin[t] < 0.125) {
      while (t + 1 < halfSize && yin[t + 1] < yin[t]) t++;
      tau = t;
      break;
    }
  }
  if (tau < 2) return null;

  // Parabolic interpolation
  let refined = tau;
  if (tau > 1 && tau < halfSize - 1) {
    const s0 = yin[tau - 1], s1 = yin[tau], s2 = yin[tau + 1];
    const denom = s0 - 2 * s1 + s2;
    if (Math.abs(denom) > 1e-8) refined = tau + (s0 - s2) / (2 * denom);
  }

  const freq = sampleRate / refined;
  // Floor at 25 Hz covers bass B0 (30.87 Hz) with headroom; ceiling covers E4
  if (freq < 25 || freq > 2000) return null;
  return freq;
}

// ── usePitchDetection hook ─────────────────────────────────────────────────

function usePitchDetection(onPitch, onMicState) {
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const activeRef = useRef(false);
  const freqBufRef = useRef([]);

  const stop = useCallback(() => {
    activeRef.current = false;
    if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    if (audioCtxRef.current) { audioCtxRef.current.close().catch(() => {}); audioCtxRef.current = null; }
    analyserRef.current = null;
    freqBufRef.current = [];
  }, []);

  const start = useCallback(async () => {
    try {
      let permState = "prompt";
      try {
        const p = await navigator.permissions.query({ name: "microphone" });
        permState = p.state;
      } catch (_) {}

      if (permState === "denied") { onMicState("denied"); return; }

      // Try with audio processing disabled first (best for pitch detection).
      // Fall back to plain audio: true on iOS / devices that reject constraints.
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
          video: false,
        });
      } catch (_) {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      }
      streamRef.current = stream;

      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      const ctx = new AudioCtx();
      if (ctx.state === "suspended") await ctx.resume();
      audioCtxRef.current = ctx;

      const analyser = ctx.createAnalyser();
      analyser.fftSize = 4096;
      analyser.smoothingTimeConstant = 0;
      analyserRef.current = analyser;

      ctx.createMediaStreamSource(stream).connect(analyser);

      activeRef.current = true;
      onMicState("granted");

      const buf = new Float32Array(4096);
      let silenceFrames = 0;
      const HOLD_FRAMES = 60; // ~1 second hold before clearing display

      function tick() {
        if (!activeRef.current || !analyserRef.current) return;
        analyserRef.current.getFloatTimeDomainData(buf);
        const freq = yinDetect(buf, ctx.sampleRate);
        if (freq !== null) {
          silenceFrames = 0;
          const hist = freqBufRef.current;
          hist.push(freq);
          if (hist.length > 5) hist.shift();
          const avg = hist.reduce((a, b) => a + b, 0) / hist.length;
          onPitch(avg);
        } else {
          silenceFrames++;
          // Hold the last reading while the string decays, then clear
          if (silenceFrames > HOLD_FRAMES) {
            freqBufRef.current = [];
            onPitch(null);
          }
        }
        rafRef.current = requestAnimationFrame(tick);
      }
      rafRef.current = requestAnimationFrame(tick);
    } catch (err) {
      if (err.name === "NotAllowedError" || err.name === "PermissionDeniedError") {
        onMicState("denied");
      } else {
        onMicState("error");
      }
    }
  }, [onPitch, onMicState]);

  // Resume the AudioContext if the browser suspended it (e.g. app backgrounded)
  const resume = useCallback(async () => {
    if (audioCtxRef.current && audioCtxRef.current.state === "suspended") {
      await audioCtxRef.current.resume().catch(() => {});
    }
  }, []);

  useEffect(() => () => stop(), [stop]);
  return { start, stop, resume };
}

// ── useReferenceTone hook ──────────────────────────────────────────────────

function useReferenceTone() {
  const ctxRef = useRef(null);
  const gainRef = useRef(null);
  const timerRef = useRef(null);

  // Resume the tone AudioContext when app comes back to foreground
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === "visible" && ctxRef.current) {
        ctxRef.current.resume().catch(() => {});
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, []);

  const stop = useCallback(() => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    if (gainRef.current && ctxRef.current) {
      try {
        const g = gainRef.current;
        const t = ctxRef.current.currentTime;
        g.gain.cancelScheduledValues(t);
        g.gain.setValueAtTime(g.gain.value, t);
        g.gain.linearRampToValueAtTime(0, t + 0.04);
      } catch (_) {}
    }
    gainRef.current = null;
  }, []);

  const play = useCallback(async (freq) => {
    stop();
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      // Always create a fresh AudioContext — called from a user gesture (tap),
      // so the new context starts in "running" state immediately. This is the
      // only reliable fix for screen-lock audio death on iOS/Android: trying
      // to resume a suspended context without a gesture always fails silently.
      if (ctxRef.current) { try { ctxRef.current.close(); } catch (_) {} }
      ctxRef.current = new AudioCtx();
      const ctx = ctxRef.current;

      const now = ctx.currentTime;

      const sustain = freq < 82 ? 8.0 : freq < 160 ? 7.0 : 5.0;
      const isBass  = freq < 82;
      const isLow   = freq < 160; // guitar E2/A2/D3 range

      // ── Loudness limiter ──────────────────────────────────────────────────
      // Drive is pushed well above 1.0; the compressor acts as a peak limiter
      // to prevent hard digital clipping.  Slow attack (10ms) lets the natural
      // pluck transient pass through cleanly; fast release (80ms) raises the
      // sustained level back to near full-scale so the note stays loud.
      const comp = ctx.createDynamicsCompressor();
      comp.threshold.value = -4;   // catch peaks near full scale
      comp.knee.value      =  4;   // tight knee for limiter-like behaviour
      comp.ratio.value     = 16;   // high ratio ≈ brickwall limiter
      comp.attack.value    = 0.010; // 10ms — lets the pick transient through
      comp.release.value   = 0.08;  // 80ms — quickly restores loudness
      // ── Stereo merger — forces both iPhone speakers to fire ──────────────
      // iPhone has two speakers (bottom + earpiece) that only both activate
      // for stereo content. Merging mono → stereo L+R ensures full speaker
      // output on iOS rather than routing to a single driver.
      const merger = ctx.createChannelMerger(2);
      merger.connect(ctx.destination);
      comp.connect(merger, 0, 0); // left channel
      comp.connect(merger, 0, 1); // right channel

      // Drive level: push hard into the limiter to stay near full-scale.
      const driveLevel = isBass ? 10.0 : 7.0;
      const masterGain = ctx.createGain();
      masterGain.gain.setValueAtTime(driveLevel, now);
      masterGain.connect(comp);
      gainRef.current = masterGain;

      // ── Harmonic partials ─────────────────────────────────────────────────
      // Amplitudes are NORMALISED — the sum of all amps ≈ 0.85–0.92 so the
      // Web Audio summing bus never clips (previous gainBoost×12 was sending
      // 10.9 into the destination → hard digital clipping → distortion).
      //
      // Harmonic weighting strategy:
      //   Upper guitar  — fundamental is in speaker range, weight h1–h3
      //   Low guitar    — phone struggles below ~150Hz, shift weight to h2–h5
      //   Bass          — phone starts hearing around 100–150Hz, weight h3–h8
      //
      // CRITICAL: every gain starts at 0 and ramps to amp in 3ms — prevents
      // the Web Audio GainNode default of 1.0 firing before the scheduled
      // event (was causing the "clap then silence" bug).
      const partials = isBass ? [
        { h: 1,  amp: 0.05, decay: sustain },
        { h: 2,  amp: 0.08, decay: sustain * 0.85 },
        { h: 3,  amp: 0.12, decay: sustain * 0.70 },
        { h: 4,  amp: 0.13, decay: sustain * 0.57 },
        { h: 5,  amp: 0.13, decay: sustain * 0.45 },
        { h: 6,  amp: 0.11, decay: sustain * 0.35 },
        { h: 7,  amp: 0.09, decay: sustain * 0.27 },
        { h: 8,  amp: 0.07, decay: sustain * 0.20 },
        { h: 9,  amp: 0.05, decay: sustain * 0.15 },
        { h: 10, amp: 0.04, decay: sustain * 0.11 },
        { h: 11, amp: 0.03, decay: sustain * 0.08 },
        { h: 12, amp: 0.02, decay: sustain * 0.06 },
      ] : isLow ? [
        { h: 1, amp: 0.12, decay: sustain },
        { h: 2, amp: 0.22, decay: sustain * 0.75 },
        { h: 3, amp: 0.20, decay: sustain * 0.57 },
        { h: 4, amp: 0.16, decay: sustain * 0.42 },
        { h: 5, amp: 0.08, decay: sustain * 0.30 },
        { h: 6, amp: 0.04, decay: sustain * 0.21 },
        { h: 7, amp: 0.02, decay: sustain * 0.15 },
        { h: 8, amp: 0.01, decay: sustain * 0.10 },
      ] : [
        { h: 1, amp: 0.28, decay: sustain },
        { h: 2, amp: 0.21, decay: sustain * 0.72 },
        { h: 3, amp: 0.15, decay: sustain * 0.52 },
        { h: 4, amp: 0.10, decay: sustain * 0.36 },
        { h: 5, amp: 0.06, decay: sustain * 0.26 },
        { h: 6, amp: 0.03, decay: sustain * 0.18 },
        { h: 7, amp: 0.01, decay: sustain * 0.13 },
        { h: 8, amp: 0.01, decay: sustain * 0.09 },
      ];

      partials.forEach(({ h, amp, decay }) => {
        const harmFreq = freq * h;
        if (harmFreq > 18000) return;
        const osc = ctx.createOscillator();
        const g   = ctx.createGain();
        osc.type = "sine";
        osc.frequency.value = harmFreq;
        // Start at 0, ramp to amp in 3ms — prevents default GainNode value
        // of 1.0 firing before the scheduled event (was causing the clap/click bug)
        g.gain.setValueAtTime(0, now);
        g.gain.linearRampToValueAtTime(amp, now + 0.020); // 20ms attack — eliminates click, compressor reacts cleanly
        g.gain.exponentialRampToValueAtTime(0.0001, now + decay);
        osc.connect(g);
        g.connect(masterGain);
        osc.start(now);
        osc.stop(now + decay + 0.05);
      });

      timerRef.current = setTimeout(() => { gainRef.current = null; }, (sustain + 0.5) * 1000);
    } catch (_) {}
  }, [stop]);

  useEffect(() => () => stop(), [stop]);
  return { play, stop };
}

// ── ArcNeedle SVG ──────────────────────────────────────────────────────────
// Semicircle opening downward: -50¢ = left, 0¢ = top, +50¢ = right

function ArcNeedle({ cents, active, inTuneLock }) {
  const cx = 160, cy = 148, r = 118;
  // angle: 0 = top (12 o'clock), positive = clockwise
  // map cents (-50..+50) → angle (-90..+90)
  const clamp = Math.max(-50, Math.min(50, cents));
  const angleDeg = (clamp / 50) * 90;
  const angleRad = (angleDeg * Math.PI) / 180;
  const dotX = cx + r * Math.sin(angleRad);
  const dotY = cy - r * Math.cos(angleRad);

  // Arc endpoints (left = -90°, right = +90°)
  const leftX = cx - r, leftY = cy;
  const rightX = cx + r, rightY = cy;

  const color = active ? centsToColor(cents) : "#2a2a28";
  const trackColor = "#1a1a18";

  // Colored fill arc from center to dot
  // For arc from top (0°) to current angle
  const fillLX = cx; // top of arc
  const fillLY = cy - r;
  const fillSweep = clamp >= 0 ? 1 : 0; // 1=clockwise for positive, 0=ccw for negative

  // Tick helper: angle in degrees from top (0=top, ±90=sides)
  const tickPt = (deg, radius) => {
    const rad = (deg * Math.PI) / 180;
    return { x: cx + radius * Math.sin(rad), y: cy - radius * Math.cos(rad) };
  };

  const ticks = [-60, -30, 30, 60]; // minor ticks
  const centerTick = 0;

  return (
    <svg viewBox="0 20 320 148" width="100%" style={{ maxWidth: 340, display: "block", overflow: "visible" }}>
      {/* Track arc */}
      <path
        d={`M ${leftX} ${leftY} A ${r} ${r} 0 0 1 ${rightX} ${rightY}`}
        fill="none" stroke={trackColor} strokeWidth="14" strokeLinecap="round"
      />

      {/* Filled arc from top to current position */}
      {active && Math.abs(clamp) > 1 && (
        <path
          d={`M ${fillLX.toFixed(2)} ${fillLY.toFixed(2)} A ${r} ${r} 0 0 ${fillSweep} ${dotX.toFixed(2)} ${dotY.toFixed(2)}`}
          fill="none" stroke={color} strokeWidth="8" strokeLinecap="round" opacity="0.5"
        />
      )}

      {/* Minor ticks */}
      {ticks.map(deg => {
        const inner = tickPt(deg, r - 7);
        const outer = tickPt(deg, r + 7);
        return (
          <line key={deg}
            x1={inner.x.toFixed(2)} y1={inner.y.toFixed(2)}
            x2={outer.x.toFixed(2)} y2={outer.y.toFixed(2)}
            stroke="#2e2e2c" strokeWidth="1.5" strokeLinecap="round"
          />
        );
      })}

      {/* Center in-tune tick (green) */}
      {(() => {
        const inner = tickPt(centerTick, r - 10);
        const outer = tickPt(centerTick, r + 10);
        return (
          <line
            x1={inner.x.toFixed(2)} y1={inner.y.toFixed(2)}
            x2={outer.x.toFixed(2)} y2={outer.y.toFixed(2)}
            stroke="#22c55e" strokeWidth="2.5" strokeLinecap="round"
          />
        );
      })()}

      {/* Flat / sharp labels at ends */}
      <text x={leftX - 14} y={leftY + 5} textAnchor="middle" fill="#3a3a38" fontSize="14" fontFamily="inherit">♭</text>
      <text x={rightX + 14} y={rightY + 5} textAnchor="middle" fill="#3a3a38" fontSize="14" fontFamily="inherit">♯</text>

      {/* Indicator dot */}
      {active && (
        <>
          {/* Dot glow */}
          <circle cx={dotX.toFixed(2)} cy={dotY.toFixed(2)} r="14"
            fill={color} opacity={inTuneLock ? "0.35" : "0.18"}
            style={{ transition: "cx 0.08s, cy 0.08s, opacity 0.2s" }}
          />
          {/* Dot */}
          <circle cx={dotX.toFixed(2)} cy={dotY.toFixed(2)} r="7"
            fill={color}
            style={{ transition: "cx 0.08s, cy 0.08s, fill 0.15s" }}
          />
          {/* Dot inner */}
          <circle cx={dotX.toFixed(2)} cy={dotY.toFixed(2)} r="3" fill="#000" opacity="0.5" />
        </>
      )}
    </svg>
  );
}

// ── HistoryTrace canvas ────────────────────────────────────────────────────

function HistoryTrace({ history }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const W = canvas.width, H = canvas.height;
    ctx.clearRect(0, 0, W, H);

    // Background
    ctx.fillStyle = "#0c0c0a";
    ctx.fillRect(0, 0, W, H);

    // Center line
    ctx.strokeStyle = "#2a2a28";
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 4]);
    ctx.beginPath();
    ctx.moveTo(0, H / 2);
    ctx.lineTo(W, H / 2);
    ctx.stroke();
    ctx.setLineDash([]);

    // ±5 zone (green zone)
    const pxPerCent = (H / 2) / 50;
    const zoneH = 5 * pxPerCent;
    ctx.fillStyle = "rgba(34,197,94,0.08)";
    ctx.fillRect(0, H / 2 - zoneH, W, zoneH * 2);

    if (history.length < 2) return;

    const totalFrames = 60;
    const xStep = W / (totalFrames - 1);

    // Draw line segments colored by value
    for (let i = 1; i < history.length; i++) {
      const x1 = (totalFrames - history.length + i - 1) * xStep;
      const x2 = (totalFrames - history.length + i) * xStep;
      const y1 = H / 2 - history[i - 1] * pxPerCent;
      const y2 = H / 2 - history[i] * pxPerCent;
      const midCents = (Math.abs(history[i - 1]) + Math.abs(history[i])) / 2;
      ctx.strokeStyle = centsToColor(midCents);
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(x1, Math.max(2, Math.min(H - 2, y1)));
      ctx.lineTo(x2, Math.max(2, Math.min(H - 2, y2)));
      ctx.stroke();
    }
  }, [history]);

  return (
    <canvas
      ref={canvasRef}
      width={320}
      height={48}
      style={{ width: "100%", maxWidth: 320, display: "block", borderRadius: 8, border: "1px solid #1a1a18" }}
    />
  );
}

// ── StringButton component ─────────────────────────────────────────────────

function StringButton({ string, index, selected, onPress, toneActive }) {
  const isSelected = selected === index;

  return (
    <button
      onClick={() => onPress(index)}
      style={{
        flex: 1,
        padding: "10px 4px",
        borderRadius: 20,
        border: `1.5px solid ${isSelected ? "#E8621A" : toneActive ? "rgba(232,98,26,0.5)" : "#242220"}`,
        background: isSelected
          ? "rgba(232,98,26,0.15)"
          : toneActive
          ? "rgba(232,98,26,0.07)"
          : "transparent",
        color: isSelected ? "#E8621A" : "#585450",
        fontFamily: "inherit",
        fontSize: 12,
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 2,
        transition: "border-color 0.12s, background 0.12s, color 0.12s",
        animation: toneActive ? "tonePulse 0.8s ease-in-out infinite alternate" : "none",
        WebkitTapHighlightColor: "transparent",
        WebkitTouchCallout: "none",
        WebkitUserSelect: "none",
        userSelect: "none",
        touchAction: "none",
      }}
    >
      <span style={{ fontSize: 16, fontWeight: "700", lineHeight: 1 }}>{string.label}</span>
      <span style={{ fontSize: 9, opacity: 0.7 }}>{string.freq.toFixed(0)} Hz</span>
    </button>
  );
}

// ── Main Tuner component ───────────────────────────────────────────────────

export default function Tuner({ defaultInstrumentType }) {
  const savedInstr = (() => {
    try { return localStorage.getItem(STORAGE_KEY); } catch (_) { return null; }
  })();
  const initInstr =
    savedInstr && INSTRUMENT_CONFIGS[savedInstr]
      ? savedInstr
      : defaultInstrumentType && INSTRUMENT_CONFIGS[defaultInstrumentType]
      ? defaultInstrumentType
      : "guitar";

  const [instrType, setInstrType] = useState(initInstr);
  const [mode, setMode] = useState("chromatic"); // "chromatic" | "target"
  const [targetIdx, setTargetIdx] = useState(null);
  const [micState, setMicState] = useState("idle"); // idle | granted | denied | error | stopped
  const [detectedFreq, setDetectedFreq] = useState(null);
  const [centsHistory, setCentsHistory] = useState([]);
  const [inTuneLock, setInTuneLock] = useState(false);
  const [tonePlayingIdx, setTonePlayingIdx] = useState(null);
  const micStateRef = useRef("idle");

  const instrTypeRef = useRef(instrType);
  const prevInTuneRef = useRef(false);
  const lockTimerRef = useRef(null);

  useEffect(() => { instrTypeRef.current = instrType; }, [instrType]);

  // ── Mic / pitch callbacks (stable refs) ──────────────────────────────────
  const onPitchRef = useRef(null);
  const onMicStateRef = useRef(null);

  onPitchRef.current = (freq) => setDetectedFreq(freq);
  onMicStateRef.current = (state) => { setMicState(state); micStateRef.current = state; };

  const stablePitch = useCallback((f) => onPitchRef.current(f), []);
  const stableMic = useCallback((s) => onMicStateRef.current(s), []);

  const { start: startMic, stop: stopMic, resume: resumeMic } = usePitchDetection(stablePitch, stableMic);
  const { play: playTone, stop: stopTone } = useReferenceTone();

  // ── iOS detection ────────────────────────────────────────────────────────
  const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);

  // ── Auto-start mic on mount ───────────────────────────────────────────────
  useEffect(() => {
    startMic();
    return () => stopMic();
  }, []); // intentional: run once on mount

  // ── Visibility change — resume or restart audio when app comes back ───────
  useEffect(() => {
    const handleVisibility = async () => {
      if (document.visibilityState !== "visible") return;
      const state = micStateRef.current;
      if (state === "stopped" || state === "idle" || state === "denied") return;
      // Try to resume the suspended AudioContext first
      await resumeMic();
      // If the stream/context was killed by the OS, restart fully
      if (state === "granted") {
        // Small delay lets the browser settle after foregrounding
        setTimeout(() => {
          stopMic();
          startMic();
        }, 300);
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, []); // intentional: stable refs used inside

  // ── Instrument switching ──────────────────────────────────────────────────
  const handleInstrChange = (key) => {
    setInstrType(key);
    setTargetIdx(null);
    setCentsHistory([]);
    setDetectedFreq(null);
    try { localStorage.setItem(STORAGE_KEY, key); } catch (_) {}
  };

  // ── Derive display values ─────────────────────────────────────────────────
  const config = INSTRUMENT_CONFIGS[instrType];

  let displayNote = null;
  let displayCents = null;
  let displayHz = null;

  if (detectedFreq) {
    if (mode === "target" && targetIdx !== null) {
      const targetFreq = config.strings[targetIdx].freq;
      const rawCents = freqToCentsFromTarget(detectedFreq, targetFreq);
      displayCents = Math.max(-50, Math.min(50, rawCents));
      displayHz = detectedFreq;
      displayNote = config.strings[targetIdx].label;
    } else {
      const note = freqToNote(detectedFreq);
      displayNote = note.name;
      displayCents = Math.max(-50, Math.min(50, note.cents));
      displayHz = detectedFreq;
    }
  }

  // ── Cents history trace ───────────────────────────────────────────────────
  useEffect(() => {
    if (displayCents === null) return;
    setCentsHistory(prev => {
      const next = [...prev, displayCents];
      return next.length > 60 ? next.slice(-60) : next;
    });
  }, [displayCents]);

  // ── In-tune lock animation ────────────────────────────────────────────────
  const isInTune = displayCents !== null && Math.abs(displayCents) <= 5;

  useEffect(() => {
    if (isInTune && !prevInTuneRef.current) {
      if (lockTimerRef.current) clearTimeout(lockTimerRef.current);
      setInTuneLock(true);
      lockTimerRef.current = setTimeout(() => setInTuneLock(false), 800);
    }
    prevInTuneRef.current = isInTune;
  }, [isInTune]);

  useEffect(() => () => { if (lockTimerRef.current) clearTimeout(lockTimerRef.current); }, []);

  // ── String button handlers ────────────────────────────────────────────────
  const handleStopStart = () => {
    if (micState === "granted") {
      stopMic();
      setMicState("stopped");
      micStateRef.current = "stopped";
      setDetectedFreq(null);
      setCentsHistory([]);
    } else {
      startMic();
    }
  };

  const handleStringPress = (idx) => {
    // Always play the reference tone on tap
    const freq = config.strings[idx].freq;
    setTonePlayingIdx(idx);
    playTone(freq);
    const decay = freq < 82 ? 4.3 : 3.1;
    setTimeout(() => setTonePlayingIdx(null), decay * 1000);

    // In target mode also select/deselect the string
    if (mode === "target") {
      setTargetIdx(idx === targetIdx ? null : idx);
    }
  };

  // ── Mode switch ───────────────────────────────────────────────────────────
  const handleModeSwitch = (m) => {
    setMode(m);
    setTargetIdx(null);
    setCentsHistory([]);
  };

  // ── Colors ────────────────────────────────────────────────────────────────
  const T = {
    bg: "#000",
    border: "#242220",
    text: "#edeae2",
    muted: "#8a8680",
    dim: "#585450",
    orange: "#E8621A",
    green: "#22c55e",
    amber: "#f59e0b",
    red: "#ef4444",
  };

  const noteColor = displayCents !== null ? centsToColor(displayCents) : T.dim;
  const centsLabel = displayCents !== null
    ? (Math.abs(displayCents) <= 5 ? "In Tune" : `${displayCents > 0 ? "+" : ""}${displayCents.toFixed(0)} cents`)
    : null;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", background: T.bg, overflowY: "auto", WebkitOverflowScrolling: "touch" }}>

      {/* Keyframe styles */}
      <style>{`
        @keyframes tonePulse {
          from { box-shadow: 0 0 0px rgba(232,98,26,0.0); }
          to   { box-shadow: 0 0 12px rgba(232,98,26,0.5); }
        }
        @keyframes lockPulse {
          0%   { transform: scale(1); }
          40%  { transform: scale(1.08); }
          100% { transform: scale(1); }
        }
      `}</style>

      {/* ── Instrument tabs ── */}
      <div style={{ display: "flex", gap: 8, padding: "12px 16px 8px", borderBottom: `1px solid ${T.border}`, flexShrink: 0 }}>
        {Object.entries(INSTRUMENT_CONFIGS).map(([key, cfg]) => {
          const active = instrType === key;
          return (
            <button key={key} onClick={() => handleInstrChange(key)} style={{
              flex: 1, padding: "9px 0", borderRadius: 10,
              border: `1px solid ${active ? T.orange : T.border}`,
              background: active ? "rgba(232,98,26,0.1)" : "transparent",
              color: active ? T.orange : T.dim,
              fontFamily: "inherit", fontSize: 13, fontWeight: active ? "600" : "normal",
              cursor: "pointer", transition: "all 0.15s",
            }}>{cfg.label}</button>
          );
        })}
      </div>


      {/* ── Mode toggle ── */}
      <div style={{ display: "flex", gap: 0, margin: "10px 16px 0", borderRadius: 10, border: `1px solid ${T.border}`, overflow: "hidden", flexShrink: 0 }}>
        {[["chromatic", "Chromatic"], ["target", "String Target"]].map(([m, label]) => {
          const active = mode === m;
          return (
            <button key={m} onClick={() => handleModeSwitch(m)} style={{
              flex: 1, padding: "9px 0",
              background: active ? "rgba(232,98,26,0.12)" : "transparent",
              color: active ? T.orange : T.dim,
              fontFamily: "inherit", fontSize: 13, fontWeight: active ? "600" : "normal",
              border: "none", borderRight: m === "chromatic" ? `1px solid ${T.border}` : "none",
              cursor: "pointer", transition: "all 0.15s",
            }}>{label}</button>
          );
        })}
      </div>

      {/* ── Mic denied message ── */}
      {(micState === "denied" || micState === "error") && (
        <div style={{ margin: "16px", padding: "14px 16px", background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.25)", borderRadius: 12, textAlign: "center" }}>
          <div style={{ fontSize: 14, color: "#ef4444", marginBottom: 8, lineHeight: 1.5 }}>
            {micState === "denied"
              ? "Microphone access is required for the tuner. Please allow mic access in your browser settings and tap Retry."
              : "Could not access microphone. Please check your device settings and tap Retry."}
          </div>
          <button onClick={startMic} style={{
            padding: "8px 20px", borderRadius: 8, border: "1px solid rgba(239,68,68,0.4)",
            background: "rgba(239,68,68,0.1)", color: "#ef4444",
            fontFamily: "inherit", fontSize: 13, cursor: "pointer",
          }}>Retry</button>
        </div>
      )}

      {/* ── Main display ── */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", padding: "8px 16px 0", minHeight: 0 }}>

        {/* Arc needle */}
        <div style={{ width: "100%", maxWidth: 340 }}>
          <ArcNeedle
            cents={displayCents ?? 0}
            active={detectedFreq !== null}
            inTuneLock={inTuneLock}
          />
        </div>

        {/* Note name */}
        <div style={{
          fontSize: 108,
          fontWeight: "800",
          lineHeight: 1,
          letterSpacing: "-0.04em",
          color: detectedFreq ? noteColor : T.dim,
          textShadow: inTuneLock ? `0 0 48px rgba(34,197,94,0.55)` : "none",
          transition: "color 0.12s, text-shadow 0.12s",
          animation: inTuneLock ? "lockPulse 0.5s ease-out" : "none",
          userSelect: "none",
          marginTop: -4,
        }}>
          {displayNote ?? "—"}
        </div>

        {/* Hz + cents row */}
        <div style={{ display: "flex", alignItems: "center", gap: 20, marginTop: 4, height: 26 }}>
          {detectedFreq ? (
            <>
              <span style={{ fontSize: 13, color: T.muted }}>Hz {displayHz.toFixed(1)}</span>
              <span style={{
                fontSize: 15, fontWeight: "700",
                color: displayCents !== null ? centsToColor(displayCents) : T.dim,
                minWidth: 80, textAlign: "center", transition: "color 0.12s",
              }}>
                {centsLabel}
              </span>
            </>
          ) : (
            <span style={{ fontSize: 13, color: T.dim }}>
              {micState === "granted" ? "Listening…" : micState === "idle" ? "Starting…" : ""}
            </span>
          )}
        </div>

        {/* History trace */}
        <div style={{ width: "100%", maxWidth: 320, marginTop: 14 }}>
          <HistoryTrace history={centsHistory} />
        </div>

        {/* Mode hint */}
        {mode === "target" && targetIdx === null && (
          <div style={{ marginTop: 8, fontSize: 12, color: T.dim, textAlign: "center" }}>
            Tap a string to select it and hear its reference tone
          </div>
        )}
        {mode === "chromatic" && (
          <div style={{ marginTop: 8, fontSize: 12, color: T.dim, textAlign: "center" }}>
            Tap any string to hear its reference tone
          </div>
        )}
      </div>

      {/* ── String buttons ── */}
      <div style={{ padding: "12px 12px 8px", flexShrink: 0 }}>
        <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
          {config.strings.map((s, i) => (
            <StringButton
              key={i}
              string={s}
              index={i}
              selected={mode === "target" ? targetIdx : null}
              onPress={handleStringPress}
              toneActive={tonePlayingIdx === i}
            />
          ))}
        </div>
      </div>

      {/* ── Start / Stop button ── */}
      <div style={{ padding: "4px 16px 28px", flexShrink: 0 }}>
        {micState === "granted" ? (
          <button onClick={handleStopStart} style={{
            width: "100%", padding: "15px 0", borderRadius: 12,
            border: `1px solid ${T.border}`, background: "#161614",
            color: T.muted, fontFamily: "inherit", fontSize: 15,
            fontWeight: "600", cursor: "pointer",
          }}>Stop</button>
        ) : (
          <button onClick={handleStopStart} style={{
            width: "100%", padding: "15px 0", borderRadius: 12,
            border: `1px solid ${T.orange}`, background: "rgba(232,98,26,0.1)",
            color: T.orange, fontFamily: "inherit", fontSize: 15,
            fontWeight: "600", cursor: "pointer",
          }}>Start Tuner</button>
        )}
      </div>

    </div>
  );
}
